import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, CloudSun, Clock, Info } from "@phosphor-icons/react";
import { getPosition, fetchWeather, fetchWeatherAt } from "../lib/geo";
import PhotoUpload from "./PhotoUpload";

function CheckRow({ checked, label, onToggle, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onToggle}
      className="w-full flex items-center gap-3 py-3 text-left border-b-2 border-line last:border-b-0"
    >
      <span
        className={`shrink-0 w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-colors ${
          checked ? "bg-teal border-teal" : "bg-white border-lineheavy"
        }`}
      >
        {checked && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M5 12l5 5L20 7" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className={`text-base leading-snug ${checked ? "text-navydark font-semibold" : "text-steel"}`}>{label}</span>
    </button>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function Field({ item, value, onChange, allData }) {
  const [busy, setBusy] = useState(false);

  switch (item.type) {
    case "info":
      return (
        <div className="flex gap-3 p-4 rounded-xl bg-tealsoft border-2 border-teal/30">
          <Info size={22} weight="bold" className="text-teal shrink-0 mt-0.5" />
          <p className="text-sm text-navy whitespace-pre-line leading-relaxed">{item.text}</p>
        </div>
      );

    case "text":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          <input
            className="uc-input"
            data-testid={`field-${item.id}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case "textarea":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          <textarea
            className="uc-input"
            data-testid={`field-${item.id}`}
            rows={3}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case "select":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          <select
            className="uc-input"
            data-testid={`field-${item.id}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">— Select —</option>
            {item.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      );

    case "datetime":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          <div className="flex gap-2">
            <input
              type="datetime-local"
              className="uc-input"
              data-testid={`field-${item.id}`}
              value={toLocalInput(value)}
              onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : "")}
            />
            <button
              type="button"
              className="uc-btn uc-btn-ghost !px-4 shrink-0"
              data-testid={`field-${item.id}-now`}
              onClick={() => onChange(new Date().toISOString())}
            >
              <Clock size={20} weight="bold" /> Now
            </button>
          </div>
        </div>
      );

    case "gps":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          {value && value.lat != null && (
            <div className="mb-2 p-3 rounded-xl bg-tealsoft border-2 border-teal/40 text-sm font-semibold text-navy">
              {value.lat.toFixed(5)}, {value.lng.toFixed(5)} &nbsp;(±{Math.round(value.accuracy)}m)
            </div>
          )}
          <button
            type="button"
            className="uc-btn uc-btn-primary w-full"
            data-testid={`field-${item.id}-capture`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const pos = await getPosition();
                onChange(pos);
                toast.success("Location captured");
              } catch (e) {
                toast.error("Could not get location: " + (e.message || "permission denied"));
              } finally {
                setBusy(false);
              }
            }}
          >
            <MapPin size={20} weight="bold" /> {value ? "Re-capture GPS" : "Capture GPS"}
          </button>
        </div>
      );

    case "weather":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          <textarea
            className="uc-input mb-2"
            data-testid={`field-${item.id}`}
            rows={2}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
          <button
            type="button"
            className="uc-btn uc-btn-ghost w-full"
            data-testid={`field-${item.id}-fetch`}
            disabled={busy}
            onClick={async () => {
              const gps = allData?.location_gps;
              let coords = gps;
              setBusy(true);
              try {
                if (!coords || coords.lat == null) coords = await getPosition();
                const w = await fetchWeather(coords.lat, coords.lng);
                onChange(w);
                toast.success("Weather fetched");
              } catch (e) {
                toast.error("Weather unavailable: " + (e.message || ""));
              } finally {
                setBusy(false);
              }
            }}
          >
            <CloudSun size={20} weight="bold" /> Auto-fetch current weather
          </button>
        </div>
      );

    case "weather_crash":
      return (
        <div>
          <label className="uc-label">{item.label}</label>
          <textarea
            className="uc-input mb-2"
            data-testid={`field-${item.id}`}
            rows={2}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
          <button
            type="button"
            className="uc-btn uc-btn-ghost w-full"
            data-testid={`field-${item.id}-fetch`}
            disabled={busy}
            onClick={async () => {
              const gps = allData?.location_gps;
              const crashTime = allData?.time_of_crash;
              if (!crashTime) {
                toast.error("Set 'Time of crash' (Inspection Details) first.");
                return;
              }
              let coords = gps;
              setBusy(true);
              try {
                if (!coords || coords.lat == null) coords = await getPosition();
                const w = await fetchWeatherAt(coords.lat, coords.lng, crashTime);
                onChange(w);
                toast.success("Crash-time weather estimated");
              } catch (e) {
                toast.error("Historical weather unavailable: " + (e.message || ""));
              } finally {
                setBusy(false);
              }
            }}
          >
            <CloudSun size={20} weight="bold" /> Auto-estimate weather at crash time
          </button>
        </div>
      );

    case "checkbox": {
      const v = value || { checked: false, note: "" };
      return (
        <div>
          <CheckRow
            testid={`field-${item.id}`}
            checked={!!v.checked}
            label={item.label}
            onToggle={() => onChange({ ...v, checked: !v.checked })}
          />
          {v.checked && (
            <input
              className="uc-input h-11 mt-2 text-sm"
              placeholder="Add a note (optional)"
              data-testid={`field-${item.id}-note`}
              value={v.note || ""}
              onChange={(e) => onChange({ ...v, note: e.target.value })}
            />
          )}
        </div>
      );
    }

    case "group": {
      const v = value || { children: {}, note: "" };
      return (
        <div className="rounded-xl border-2 border-line p-4 bg-white">
          <p className="font-head font-semibold text-navy mb-1">{item.label}</p>
          {item.children.map((c) => (
            <CheckRow
              key={c.id}
              testid={`field-${item.id}-${c.id}`}
              checked={!!(v.children || {})[c.id]}
              label={c.label}
              onToggle={() =>
                onChange({ ...v, children: { ...(v.children || {}), [c.id]: !(v.children || {})[c.id] } })
              }
            />
          ))}
          <input
            className="uc-input h-11 mt-3 text-sm"
            placeholder="Notes for this group (optional)"
            data-testid={`field-${item.id}-note`}
            value={v.note || ""}
            onChange={(e) => onChange({ ...v, note: e.target.value })}
          />
        </div>
      );
    }

    case "photos":
      return (
        <PhotoUpload photos={value || []} onChange={onChange} />
      );

    default:
      return null;
  }
}
